import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  createEmployee,
  listEmployeesFull,
  listCustomFieldSchemas,
  updateEmployee,
} from "@/api/lookups";
import type {
  EmployeeCreate,
  EmployeeResponse,
  EmployeeSummary,
  EmployeeUpdate,
  UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderPicker } from "@/components/folder/FolderPicker";
import {
  CustomFieldsForm,
  validateCustomFields,
} from "@/components/registries/CustomFieldsForm";
import {
  DuplicateSuggestionsDialog,
  isSimilarCandidate,
  type DuplicateCandidate,
} from "@/components/registries/DuplicateSuggestionsDialog";

export interface EmployeeFormDialogProps {
  open: boolean;
  existing: EmployeeResponse | null;
  onClose: () => void;
  /** Prefill `full_name` for the inline-create flow. */
  initialName?: string;
  /** Fired with the saved entity when the inline-create succeeds. */
  onSaved?: (saved: EmployeeSummary) => void;
}

const schema = z.object({
  full_name: z.string().min(1).max(255),
  position: z.string().optional(),
  department: z.string().optional(),
  aliases: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function parseAliases(raw: string | undefined): string[] {
  if (raw === undefined || raw.length === 0) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** True when two id sets have identical members (order-independent). */
function sameIdSet(a: readonly UUID[], b: readonly UUID[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

export function EmployeeFormDialog({
  open,
  existing,
  onClose,
  initialName,
  onSaved,
}: EmployeeFormDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "",
      position: "",
      department: "",
      aliases: "",
    },
  });
  const [customValues, setCustomValues] = useState<Record<string, unknown>>({});
  const [cfErrors, setCfErrors] = useState<Record<string, string>>({});
  /**
   * Folder memberships (TM-5d6213f1). Multi-folder model: a single
   * employee may belong to N folders. The form requires ≥1 — the submit
   * button is disabled until at least one box is ticked.
   */
  const [folderIds, setFolderIds] = useState<UUID[]>([]);
  /**
   * Pending submit captured between the duplicate-check and the
   * user's decision. See ClientFormDialog for the full rationale; the
   * Employee flow uses the same UX pattern keyed on `full_name`.
   */
  const [dupState, setDupState] = useState<{
    values: FormValues;
    candidates: DuplicateCandidate[];
  } | null>(null);

  const schemas = useQuery({
    queryKey: ["custom-fields", "employee"],
    queryFn: () => listCustomFieldSchemas("employee"),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setCfErrors({});
    setDupState(null);
    if (existing === null) {
      form.reset({
        full_name: initialName ?? "",
        position: "",
        department: "",
        aliases: "",
      });
      setCustomValues({});
      setFolderIds([]);
    } else {
      form.reset({
        full_name: existing.full_name,
        position: existing.position ?? "",
        department: existing.department ?? "",
        aliases: existing.aliases.join(", "),
      });
      setCustomValues(existing.custom_field_values ?? {});
      setFolderIds(existing.folder_ids);
    }
  }, [open, existing, form, initialName]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const basePayload = {
        full_name: values.full_name,
        position: values.position === "" ? null : values.position,
        department: values.department === "" ? null : values.department,
        aliases: parseAliases(values.aliases),
        custom_field_values: customValues,
      };
      if (existing === null) {
        // TM-5d6213f1: `folder_ids` is mandatory on create (BE enforces
        // min_length=1). The submit button is gated below so the array
        // is guaranteed non-empty here.
        const createBody: EmployeeCreate = {
          ...basePayload,
          folder_ids: folderIds,
        };
        return createEmployee(createBody);
      }
      // PATCH `folder_ids` only when the operator actually changed the
      // membership set — otherwise the BE wastes a DELETE/INSERT
      // transaction. Order-independent equality keeps a no-op edit truly
      // no-op even when the picker re-orders the array.
      const updateBody: EmployeeUpdate = { ...basePayload };
      if (!sameIdSet(folderIds, existing.folder_ids)) {
        updateBody.folder_ids = folderIds;
      }
      return updateEmployee(existing.id, updateBody);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["employees"] });
      if (onSaved !== undefined) {
        const summary: EmployeeSummary = {
          id: saved.id,
          full_name: saved.full_name,
          aliases: saved.aliases,
        };
        onSaved(summary);
      }
      onClose();
    },
  });

  async function onSubmit(values: FormValues) {
    const errs = validateCustomFields(schemas.data ?? [], customValues);
    setCfErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (folderIds.length === 0) return;
    if (existing === null) {
      const candidates = await fetchDuplicateCandidates(values.full_name);
      if (candidates.length > 0) {
        setDupState({ values, candidates });
        return;
      }
    }
    mutation.mutate(values);
  }

  /**
   * Same shape as the client variant but keyed on `full_name`. The backend
   * ILIKE search hits both name+aliases; we then run the FE normalize check
   * to filter out trivial overlaps.
   */
  async function fetchDuplicateCandidates(
    fullName: string,
  ): Promise<DuplicateCandidate[]> {
    try {
      const results = await listEmployeesFull({ search: fullName, limit: 10 });
      return results
        .filter((e) =>
          isSimilarCandidate(fullName, {
            name: e.full_name,
            aliases: e.aliases,
          }),
        )
        .slice(0, 3)
        .map((e) => ({ id: e.id, name: e.full_name, aliases: e.aliases }));
    } catch {
      return [];
    }
  }

  function handlePickExisting(id: string): void {
    if (onSaved !== undefined) {
      const picked = dupState?.candidates.find((c) => c.id === id);
      if (picked !== undefined) {
        onSaved({
          id: picked.id,
          full_name: picked.name,
          aliases: picked.aliases,
        });
      }
    }
    setDupState(null);
    onClose();
  }

  function handleCreateAnyway(): void {
    const pending = dupState;
    setDupState(null);
    if (pending !== null) mutation.mutate(pending.values);
  }

  const submitDisabled = mutation.isPending || folderIds.length === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {existing === null
                ? t("employees.createButton")
                : t("common.edit")}
            </DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={form.handleSubmit(onSubmit)}
            data-testid="employee-form"
          >
            <div>
              <Label htmlFor="emp-name">{t("employees.fields.fullName")}</Label>
              <Input
                id="emp-name"
                {...form.register("full_name")}
                aria-invalid={form.formState.errors.full_name !== undefined}
              />
              {form.formState.errors.full_name !== undefined ? (
                <p className="text-xs text-destructive">
                  {t("employees.errors.fullNameRequired")}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="emp-position">
                {t("employees.fields.position")}
              </Label>
              <Input id="emp-position" {...form.register("position")} />
            </div>
            <div>
              <Label htmlFor="emp-department">
                {t("employees.fields.department")}
              </Label>
              <Input id="emp-department" {...form.register("department")} />
            </div>
            <div>
              <Label htmlFor="emp-aliases">
                {t("employees.fields.aliases")}
              </Label>
              <Input id="emp-aliases" {...form.register("aliases")} />
            </div>
            <div>
              <Label htmlFor="emp-folders">
                {t("employees.fields.folders")}
              </Label>
              <FolderPicker
                id="emp-folders"
                multi
                value={folderIds}
                onChange={setFolderIds}
              />
              {folderIds.length === 0 ? (
                <p
                  className="text-xs text-destructive"
                  data-testid="employee-folders-required"
                >
                  {t("employees.errors.foldersRequired")}
                </p>
              ) : null}
            </div>
            <CustomFieldsForm
              schemas={schemas.data ?? []}
              values={customValues}
              onChange={setCustomValues}
              errors={cfErrors}
              idPrefix="emp-cf"
            />
            {mutation.isError ? (
              <p className="text-xs text-destructive" role="alert">
                {t("employees.errors.saveFailed")}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={submitDisabled}>
                {mutation.isPending ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {dupState !== null ? (
        <DuplicateSuggestionsDialog
          open
          query={dupState.values.full_name}
          candidates={dupState.candidates}
          onPickExisting={handlePickExisting}
          onCreateAnyway={handleCreateAnyway}
          onCancel={() => setDupState(null)}
        />
      ) : null}
    </>
  );
}
